import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, Settings, X } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import {
  DateRangeFilter,
  LIST_FILTER_DATE_RANGE_PRESETS,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { DataTable, type DataTableColumn, type SortDirection } from '@/components/shared/data-table';
import { FileDropzone } from '@/components/shared/file-dropzone';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { ImageLightbox } from '@/components/shared/image-lightbox';
import { Pagination } from '@/components/shared/pagination';
import { PermissionGate } from '@/components/shared/permission-gate';
import { EmptyState, ErrorState } from '@/components/shared/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { SwitchField } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api, type Collection } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { uploadBlob } from '@/lib/upload';
import { PageHeader } from '@/components/shared/page-header';
import { SearchInput } from '@/components/shared/search-input';
import { ChronicleCategoriesDialog } from '@/pages/content/chronicle-categories-dialog';
import { chronicleMonthLabel, formatChronicleDate } from '@/utils/format';

const CREATE_CATEGORY = '__create_chronicle_category__';
const PAGE_SIZE = 25;
const MAX_FILES = 20;
const MAX_FILE_BYTES = 25 * 1024 * 1024;

const emptyChronicleDateRange = (): DateRange => ({ preset: 'allTime', from: null, to: null });

type Precision = 'day' | 'month' | 'year';
type StoryStatus = 'draft' | 'published';
type ChronicleView = 'timeline' | 'list';

interface ChronicleCategory {
  id: string;
  name: string;
  momentCount: number;
}

interface ChronicleAttachment {
  id: string;
  fileName: string;
  contentType: string;
  kind: 'image' | 'document';
  sortOrder: number;
  downloadUrl: string | null;
}

interface ChronicleEntry {
  id: string;
  title: string;
  body: string | null;
  occurredOn: string;
  datePrecision: Precision;
  categoryId: string;
  categoryName: string;
  isPublic: boolean;
  status: StoryStatus;
  attachments: ChronicleAttachment[];
}

interface ChronicleSettings {
  isPublic: boolean;
}

const PRECISIONS: Precision[] = ['day', 'month', 'year'];

const monthValue = (month: number): string => String(month).padStart(2, '0');

type ChroniclePreviewFile = {
  id: string;
  fileName: string;
  kind: 'image' | 'document';
  url: string | null;
  contentType: string;
};

const isPdfFile = (contentType: string, fileName: string) =>
  contentType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');

const IMAGE_FILE = /\.(jpe?g|png|webp|heic|heif)$/i;

const fileIsImage = (file: ChroniclePreviewFile) =>
  file.kind === 'image' ||
  file.contentType.toLowerCase().startsWith('image/') ||
  IMAGE_FILE.test(file.fileName);

const pendingFileKind = (file: File): 'image' | 'document' =>
  file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)
    ? 'image'
    : 'document';

/** Keeps the moment dialog open while a photo or document preview sits above it. */
const blockDialogDismiss = (event: { preventDefault: () => void }) => {
  event.preventDefault();
};

const previewDismissLock = (previewOpen: boolean) =>
  previewOpen
    ? {
        onPointerDownOutside: blockDialogDismiss,
        onInteractOutside: blockDialogDismiss,
        onFocusOutside: blockDialogDismiss,
        onEscapeKeyDown: blockDialogDismiss,
      }
    : {};

const ChronicleDocumentPreview = ({
  file,
  onClose,
  closeLabel,
  openLabel,
  externalLabel,
}: {
  file: ChroniclePreviewFile;
  onClose: () => void;
  closeLabel: string;
  openLabel: string;
  externalLabel: string;
}) => {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const pdf = file.url ? isPdfFile(file.contentType, file.fileName) : false;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/88 px-3 pb-6 pt-14 backdrop-blur-sm sm:px-4"
      role="dialog"
      aria-modal="true"
      aria-label={file.fileName}
      onClick={onClose}
    >
      <div className="absolute inset-x-0 top-0 flex items-center justify-end px-4 py-3">
        <button
          type="button"
          className="rounded-lg border border-white/35 bg-slate-900/55 px-3.5 py-2 text-body-sm font-semibold text-white transition-colors hover:bg-slate-800/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
        >
          {closeLabel}
        </button>
      </div>
      {pdf && file.url ? (
        <iframe
          title={file.fileName}
          src={file.url}
          className="h-[min(80dvh,48rem)] w-full max-w-4xl rounded-lg bg-surface"
          onClick={(event) => event.stopPropagation()}
        />
      ) : (
        <div
          className="flex max-w-md flex-col items-center gap-3 rounded-lg bg-surface p-6 text-center"
          onClick={(event) => event.stopPropagation()}
        >
          <FileText aria-hidden className="size-8 text-fg-tertiary" />
          <p className="text-body font-medium text-fg">{file.fileName}</p>
          <p className="text-body-sm text-fg-secondary">{externalLabel}</p>
          {file.url ? (
            <Button asChild>
              <a href={file.url} target="_blank" rel="noopener noreferrer">
                {openLabel}
              </a>
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
};

const ChronicleFileTiles = ({
  files,
  onRemove,
  removingId,
  onPreviewChange,
}: {
  files: ChroniclePreviewFile[];
  onRemove?: (id: string) => void;
  removingId?: string | null;
  onPreviewChange?: (open: boolean) => void;
}) => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const images = files.filter((file) => fileIsImage(file));
  const previewImages = images.filter((file) => file.url);
  const documents = files.filter((file) => !fileIsImage(file));
  const imageIndex =
    lightboxIndex !== null && lightboxIndex < previewImages.length ? lightboxIndex : null;
  const documentFile = documents.find((file) => file.id === documentId) ?? null;
  const previewing = imageIndex !== null || documentFile !== null;

  useLayoutEffect(() => {
    onPreviewChange?.(previewing);
  }, [onPreviewChange, previewing]);

  useLayoutEffect(() => () => onPreviewChange?.(false), [onPreviewChange]);

  const closePreview = () => {
    setLightboxIndex(null);
    setDocumentId(null);
    onPreviewChange?.(false);
  };

  const openImage = (file: ChroniclePreviewFile) => {
    const index = previewImages.findIndex((item) => item.id === file.id);
    if (index < 0) return;
    setDocumentId(null);
    setLightboxIndex(index);
    onPreviewChange?.(true);
  };

  const openDocument = (id: string) => {
    const file = documents.find((item) => item.id === id);
    if (!file?.url) return;
    setLightboxIndex(null);
    setDocumentId(id);
    onPreviewChange?.(true);
  };

  const removeTile = (file: ChroniclePreviewFile) => {
    if (!onRemove) return;
    if (previewImages[imageIndex ?? -1]?.id === file.id || documentFile?.id === file.id) closePreview();
    onRemove(file.id);
  };

  if (files.length === 0) return null;

  return (
    <>
      <div className="flex flex-col gap-3">
        {images.length > 0 ? (
          <ul className="grid grid-cols-4 gap-2 sm:grid-cols-5">
            {images.map((file) => (
              <li key={file.id} className="relative">
                <button
                  type="button"
                  className="block aspect-square w-full cursor-zoom-in overflow-hidden rounded-lg bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  aria-label={t('chronicle.file.preview', { name: file.fileName })}
                  disabled={!file.url}
                  onClick={() => openImage(file)}
                >
                  {file.url ? (
                    <img src={file.url} alt="" className="size-full object-contain" />
                  ) : (
                    <span className="flex size-full items-center justify-center p-1 text-center text-caption text-fg-secondary">
                      {file.fileName}
                    </span>
                  )}
                </button>
                {onRemove ? (
                  <button
                    type="button"
                    className="absolute end-1 top-1 flex size-6 items-center justify-center rounded-md bg-surface/90 text-fg shadow-e1"
                    aria-label={t('chronicle.file.remove')}
                    disabled={Boolean(removingId)}
                    onClick={() => removeTile(file)}
                  >
                    {removingId === file.id ? (
                      <Spinner className="size-3.5" />
                    ) : (
                      <X className="size-3.5" aria-hidden />
                    )}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {documents.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {documents.map((file) => (
              <li
                key={file.id}
                className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5"
              >
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-2 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  aria-label={t('chronicle.file.preview', { name: file.fileName })}
                  disabled={!file.url}
                  onClick={() => openDocument(file.id)}
                >
                  <FileText aria-hidden className="size-4 shrink-0 text-fg-tertiary" />
                  <span className="truncate text-body-sm text-fg">{file.fileName}</span>
                </button>
                {onRemove ? (
                  <button
                    type="button"
                    className="flex size-6 shrink-0 items-center justify-center rounded-md text-fg-tertiary hover:text-fg"
                    aria-label={t('chronicle.file.remove')}
                    disabled={Boolean(removingId)}
                    onClick={() => removeTile(file)}
                  >
                    {removingId === file.id ? (
                      <Spinner className="size-3.5" />
                    ) : (
                      <X className="size-3.5" aria-hidden />
                    )}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {previewing
        ? createPortal(
            <>
              <ImageLightbox
                images={previewImages.map((file) => file.url ?? '')}
                index={imageIndex}
                onClose={closePreview}
                onIndexChange={setLightboxIndex}
                caption={imageIndex !== null ? previewImages[imageIndex]?.fileName : null}
                labels={{
                  dialogLabel: tCommon('lightbox.title'),
                  close: tCommon('lightbox.close'),
                  previous: tCommon('lightbox.previous'),
                  next: tCommon('lightbox.next'),
                  count:
                    imageIndex !== null
                      ? tCommon('lightbox.imageCount', {
                          current: imageIndex + 1,
                          total: previewImages.length,
                        })
                      : '',
                  imageAlt:
                    imageIndex !== null
                      ? t('chronicle.file.preview', {
                          name: previewImages[imageIndex]?.fileName ?? '',
                        })
                      : '',
                }}
              />
              {documentFile ? (
                <ChronicleDocumentPreview
                  file={documentFile}
                  onClose={closePreview}
                  closeLabel={tCommon('lightbox.close')}
                  openLabel={t('chronicle.file.open')}
                  externalLabel={t('chronicle.file.external')}
                />
              ) : null}
            </>,
            document.body,
          )
        : null}
    </>
  );
};

export const ChurchStoryPage = () => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const membership = useCurrentMembership();
  const canPublish = hasPermission(membership, PERMISSIONS.CONTENT_PUBLISH);
  const canCreate = hasPermission(membership, PERMISSIONS.CONTENT_CREATE);
  const canReport = hasPermission(membership, PERMISSIONS.REPORTS_VIEW);

  const [page, setPage] = useState(1);
  const [view, setView] = useState<ChronicleView>('timeline');
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sortField, setSortField] = useState('occurredOn');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [status, setStatus] = useState<StoryStatus | null>(null);
  const [visibility, setVisibility] = useState<'both' | 'public' | 'private'>('both');
  const [search, setSearch] = useState('');
  const [filterDateRange, setFilterDateRange] = useState<DateRange>(emptyChronicleDateRange);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailFilePreview, setDetailFilePreview] = useState(false);
  const [editFilePreview, setEditFilePreview] = useState(false);
  const filePreviewOpen = detailFilePreview || editFilePreview;
  const [deleteTarget, setDeleteTarget] = useState<ChronicleEntry | null>(null);
  const [unpublishTarget, setUnpublishTarget] = useState<ChronicleEntry | null>(null);

  const categories = useQuery({
    queryKey: ['content', 'chronicle', 'categories'],
    queryFn: () =>
      api.get<Collection<ChronicleCategory>>('/content/chronicle/categories', {
        query: { page: 1, pageSize: 100 },
      }),
  });

  const settings = useQuery({
    queryKey: ['content', 'chronicle', 'settings'],
    queryFn: () => api.get<ChronicleSettings>('/content/chronicle/settings'),
  });

  const list = useQuery({
    queryKey: [
      'content',
      'chronicle',
      page,
      sortField,
      sortDirection,
      categoryId,
      status,
      visibility,
      search,
      filterDateRange.from,
      filterDateRange.to,
    ],
    queryFn: () =>
      api.get<Collection<ChronicleEntry>>('/content/chronicle', {
        query: {
          page,
          pageSize: PAGE_SIZE,
          sort: sortDirection === 'desc' ? `-${sortField}` : sortField,
          categoryId: categoryId ?? undefined,
          status: status ?? undefined,
          visibility: visibility === 'both' ? undefined : visibility,
          q: search.trim() || undefined,
          dateFrom: filterDateRange.from ?? undefined,
          dateTo: filterDateRange.to ?? undefined,
        },
      }),
  });

  const saveSettings = useMutation({
    mutationFn: (isPublic: boolean) =>
      api.patch<ChronicleSettings>('/content/chronicle/settings', { isPublic }),
    onSuccess: () => {
      toast.success(t('chronicle.settingsSaved'));
      void queryClient.invalidateQueries({ queryKey: ['content', 'chronicle', 'settings'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const publish = useMutation({
    mutationFn: (id: string) => api.post<ChronicleEntry>(`/content/chronicle/${id}/publish`),
    onSuccess: () => {
      toast.success(t('chronicle.published'));
      void queryClient.invalidateQueries({ queryKey: ['content', 'chronicle'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const unpublish = useMutation({
    mutationFn: (id: string) => api.post<ChronicleEntry>(`/content/chronicle/${id}/unpublish`),
    onSuccess: () => {
      toast.success(t('chronicle.unpublished'));
      setUnpublishTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['content', 'chronicle'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/content/chronicle/${id}`),
    onSuccess: () => {
      toast.success(t('chronicle.deleted'));
      setDeleteTarget(null);
      setDetailId(null);
      void queryClient.invalidateQueries({ queryKey: ['content', 'chronicle'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const rows = list.data?.data ?? [];
  const hasFilters =
    categoryId !== null ||
    status !== null ||
    visibility !== 'both' ||
    search.trim().length > 0 ||
    filterDateRange.from !== null ||
    filterDateRange.to !== null;
  const categoryOptions = categories.data?.data ?? [];

  const dateFilterLabels = {
    today: tCommon('date.today'),
    yesterday: tCommon('date.yesterday'),
    thisWeek: tCommon('date.thisWeek'),
    lastWeek: tCommon('date.lastWeek'),
    thisMonth: tCommon('date.thisMonth'),
    lastMonth: tCommon('date.lastMonth'),
    thisQuarter: tCommon('date.thisQuarter'),
    thisYear: tCommon('date.thisYear'),
    lastYear: tCommon('date.lastYear'),
    allTime: tCommon('date.allTime'),
    custom: tCommon('date.custom'),
    from: t('chronicle.filters.dateFrom'),
    to: t('chronicle.filters.dateTo'),
  } as const;

  const clearFilters = () => {
    setCategoryId(null);
    setStatus(null);
    setVisibility('both');
    setSearch('');
    setFilterDateRange(emptyChronicleDateRange());
    setPage(1);
  };

  const columns: DataTableColumn<ChronicleEntry>[] = [
    {
      id: 'occurredOn',
      header: t('chronicle.columns.date'),
      sortable: true,
      cell: (row) => formatChronicleDate(row.occurredOn, row.datePrecision),
    },
    {
      id: 'title',
      header: t('chronicle.columns.title'),
      sortable: true,
      cell: (row) => row.title,
    },
    {
      id: 'category',
      header: t('chronicle.columns.category'),
      hideBelowMd: true,
      cell: (row) => row.categoryName,
    },
    {
      id: 'status',
      header: t('chronicle.columns.status'),
      cell: (row) => (
        <Badge variant={row.status === 'published' ? 'success' : 'neutral'}>
          {t(`chronicle.status.${row.status}`)}
        </Badge>
      ),
    },
    {
      id: 'visibility',
      header: t('chronicle.columns.visibility'),
      hideBelowMd: true,
      cell: (row) => t(row.isPublic ? 'chronicle.visibility.public' : 'chronicle.visibility.private'),
    },
  ];

  const openCreate = () => {
    setEditingId(null);
    setDialogOpen(true);
  };

  const openDetail = (row: ChronicleEntry) => {
    setDetailId(row.id);
  };

  const openEdit = (row: ChronicleEntry) => {
    setEditingId(row.id);
    setDialogOpen(true);
  };

  return (
    <div>
      <PageHeader
        title={t('chronicle.title')}
        description={t('chronicle.description')}
        headerActions={
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t('chronicle.settings.open')}
              onClick={() => setSettingsOpen(true)}
            >
              <Settings className="size-4" aria-hidden />
            </Button>
            {canReport ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/reports?report=chronicle_moments')}
              >
                {t('chronicle.report.open')}
              </Button>
            ) : null}
            <Button type="button" variant="outline" onClick={() => setCategoriesOpen(true)}>
              {t('chronicle.categories.open')}
            </Button>
            {canCreate ? (
              <Button type="button" onClick={openCreate}>
                {t('chronicle.add')}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="mb-4 space-y-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
          <FormField
            label={t('chronicle.filters.search')}
            className="min-w-0 flex-1 sm:max-w-xs"
          >
            {(field) => (
              <SearchInput
                id={field.id}
                value={search}
                onChange={(value) => {
                  setSearch(value);
                  setPage(1);
                }}
                placeholder={t('chronicle.filters.searchPlaceholder')}
                clearLabel={tCommon('search.clear')}
                aria-describedby={field['aria-describedby']}
                aria-label={t('chronicle.filters.search')}
              />
            )}
          </FormField>
          <DateRangeFilter
            value={filterDateRange}
            onChange={(range) => {
              setFilterDateRange(range);
              setPage(1);
            }}
            labels={dateFilterLabels}
            presets={LIST_FILTER_DATE_RANGE_PRESETS}
          />
        </div>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
        <div className="grid flex-1 gap-4 sm:grid-cols-3">
        <FormField label={t('chronicle.filters.category')}>
            {(field) => (
              <Listbox
                id={field.id}
                value={categoryId}
                onChange={(value) => {
                  setCategoryId(value);
                  setPage(1);
                }}
                options={categoryOptions.map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
                placeholder={t('chronicle.filters.allCategories')}
                clearable
                clearLabel={t('chronicle.filters.clear')}
                aria-describedby={field['aria-describedby']}
              />
            )}
          </FormField>
          <FormField label={t('chronicle.filters.status')}>
            {(field) => (
              <Listbox
                id={field.id}
                value={status}
                onChange={(value) => {
                  setStatus(value);
                  setPage(1);
                }}
                options={(['draft', 'published'] as const).map((item) => ({
                  value: item,
                  label: t(`chronicle.status.${item}`),
                }))}
                placeholder={t('chronicle.filters.allStatuses')}
                clearable
                clearLabel={t('chronicle.filters.clear')}
                aria-describedby={field['aria-describedby']}
              />
            )}
          </FormField>
          <FormField label={t('chronicle.filters.visibility')}>
            {(field) => (
              <Listbox
                id={field.id}
                value={visibility}
                onChange={(value) => {
                  if (value) setVisibility(value);
                  setPage(1);
                }}
                options={[
                  { value: 'both' as const, label: t('chronicle.visibility.both') },
                  { value: 'public' as const, label: t('chronicle.visibility.public') },
                  { value: 'private' as const, label: t('chronicle.visibility.private') },
                ]}
                aria-describedby={field['aria-describedby']}
              />
            )}
          </FormField>
        </div>
        <div
          className="flex h-10 shrink-0 rounded-lg border border-border p-0.5"
          role="group"
          aria-label={t('chronicle.view.label')}
        >
          <Button
            type="button"
            size="sm"
            variant={view === 'timeline' ? 'primary' : 'ghost'}
            aria-pressed={view === 'timeline'}
            onClick={() => setView('timeline')}
          >
            {t('chronicle.view.timeline')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={view === 'list' ? 'primary' : 'ghost'}
            aria-pressed={view === 'list'}
            onClick={() => setView('list')}
          >
            {t('chronicle.view.list')}
          </Button>
        </div>
        </div>
      </div>

      {list.isError ? (
        <ErrorState
          title={tErrors('UNEXPECTED')}
          onRetry={() => void list.refetch()}
          retryLabel={tCommon('action.retry')}
        />
      ) : list.isLoading ? (
        <DataTable rows={[]} columns={columns} rowKey={() => ''} loading />
      ) : rows.length === 0 ? (
        <EmptyState
          title={hasFilters ? tCommon('state.noFilterResults') : t('chronicle.empty.title')}
          body={hasFilters ? tCommon('state.noFilterResultsBody') : t('chronicle.empty.body')}
          action={
            hasFilters ? (
              <Button type="button" variant="outline" onClick={clearFilters}>
                {t('chronicle.filters.clear')}
              </Button>
            ) : canCreate ? (
              <Button type="button" onClick={openCreate}>
                {t('chronicle.add')}
              </Button>
            ) : null
          }
        />
      ) : view === 'list' ? (
        <>
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            onRowClick={openDetail}
            sort={{
              field: sortField,
              direction: sortDirection,
              onChange: (field, direction) => {
                setSortField(field);
                setSortDirection(direction);
                setPage(1);
              },
            }}
          />
          {list.data && list.data.page.totalPages > 1 ? (
            <div className="mt-4">
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                totalItems={list.data.page.totalItems}
                totalPages={list.data.page.totalPages}
                onPageChange={setPage}
                labels={{
                  showing: ({ from, to, total }) =>
                    tCommon('pagination.showing', { from, to, total }),
                  perPage: tCommon('pagination.perPage'),
                  first: tCommon('pagination.first'),
                  previous: tCommon('pagination.previous'),
                  next: tCommon('pagination.next'),
                  last: tCommon('pagination.last'),
                }}
              />
            </div>
          ) : null}
        </>
      ) : (
        <>
          <ChronicleTimeline rows={rows} onOpen={openDetail} />
          {list.data && list.data.page.totalPages > 1 ? (
            <div className="mt-4">
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                totalItems={list.data.page.totalItems}
                totalPages={list.data.page.totalPages}
                onPageChange={setPage}
                labels={{
                  showing: ({ from, to, total }) =>
                    tCommon('pagination.showing', { from, to, total }),
                  perPage: tCommon('pagination.perPage'),
                  first: tCommon('pagination.first'),
                  previous: tCommon('pagination.previous'),
                  next: tCommon('pagination.next'),
                  last: tCommon('pagination.last'),
                }}
              />
            </div>
          ) : null}
        </>
      )}

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('chronicle.settings.title')}</DialogTitle>
          </DialogHeader>
          <DialogBody contentClassName="flex flex-col gap-4">
            <SwitchField
              id="chronicle-public"
              label={t('chronicle.settings.publicLabel')}
              description={t('chronicle.settings.publicDescription')}
              checked={settings.data?.isPublic ?? false}
              disabled={!canPublish || saveSettings.isPending || settings.isLoading}
              onCheckedChange={(checked) => saveSettings.mutate(checked)}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>

      <ChronicleCategoriesDialog open={categoriesOpen} onOpenChange={setCategoriesOpen} />

      <MomentDetailDialog
        entryId={detailId}
        filePreviewOpen={filePreviewOpen}
        onFilePreviewChange={setDetailFilePreview}
        onOpenChange={(next) => {
          if (!next) setDetailId(null);
        }}
        onEdit={(entry) => openEdit(entry)}
        onPublish={(id) => publish.mutate(id)}
        onUnpublish={setUnpublishTarget}
        onDelete={setDeleteTarget}
        publishing={publish.isPending}
      />

      <MomentDialog
        open={dialogOpen}
        entryId={editingId}
        categories={categoryOptions}
        canPublish={canPublish}
        filePreviewOpen={filePreviewOpen}
        onFilePreviewChange={setEditFilePreview}
        onOpenChange={setDialogOpen}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ['content', 'chronicle'] });
        }}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t('chronicle.confirm.deleteTitle', { title: deleteTarget?.title ?? '' })}
        description={t('chronicle.confirm.deleteBody')}
        confirmLabel={t('chronicle.confirm.delete')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
        }}
      />

      <ConfirmDialog
        open={unpublishTarget !== null}
        onOpenChange={(open) => {
          if (!open) setUnpublishTarget(null);
        }}
        title={t('chronicle.confirm.unpublishTitle', { title: unpublishTarget?.title ?? '' })}
        description={t('chronicle.confirm.unpublishBody')}
        confirmLabel={t('chronicle.confirm.unpublish')}
        cancelLabel={tCommon('action.cancel')}
        loading={unpublish.isPending}
        onConfirm={() => {
          if (unpublishTarget) unpublish.mutate(unpublishTarget.id);
        }}
      />
    </div>
  );
};

const MomentDetailDialog = ({
  entryId,
  filePreviewOpen,
  onFilePreviewChange,
  onOpenChange,
  onEdit,
  onPublish,
  onUnpublish,
  onDelete,
  publishing,
}: {
  entryId: string | null;
  filePreviewOpen: boolean;
  onFilePreviewChange: (open: boolean) => void;
  onOpenChange: (open: boolean) => void;
  onEdit: (entry: ChronicleEntry) => void;
  onPublish: (id: string) => void;
  onUnpublish: (entry: ChronicleEntry) => void;
  onDelete: (entry: ChronicleEntry) => void;
  publishing: boolean;
}) => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const detail = useQuery({
    queryKey: ['content', 'chronicle', 'entry', entryId],
    enabled: entryId !== null,
    queryFn: () => api.get<ChronicleEntry>(`/content/chronicle/${entryId}`),
  });
  const entry = detail.data;

  return (
    <Dialog open={entryId !== null} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        closeLabel={tCommon('action.close')}
        className="overflow-visible"
        {...previewDismissLock(filePreviewOpen)}
      >
        <DialogHeader>
          <DialogTitle>{entry?.title ?? t('chronicle.title')}</DialogTitle>
        </DialogHeader>
        {entry ? (
          <>
            <DialogBody contentClassName="flex flex-col gap-4">
              <p className="text-caption text-fg-secondary">
                {formatChronicleDate(entry.occurredOn, entry.datePrecision)}
                {' · '}
                {entry.categoryName}
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant={entry.status === 'published' ? 'success' : 'neutral'}>
                  {t(`chronicle.status.${entry.status}`)}
                </Badge>
                <Badge variant="neutral">
                  {t(entry.isPublic ? 'chronicle.visibility.public' : 'chronicle.visibility.private')}
                </Badge>
              </div>
              <p className="whitespace-pre-wrap text-body text-fg">
                {entry.body?.trim() ? entry.body : '—'}
              </p>
              <ChronicleFileTiles
                files={entry.attachments.map((file) => ({
                  id: file.id,
                  fileName: file.fileName,
                  kind: file.kind,
                  url: file.downloadUrl,
                  contentType: file.contentType,
                }))}
                onPreviewChange={onFilePreviewChange}
              />
            </DialogBody>
            <DialogFooter>
              <PermissionGate permission={PERMISSIONS.CONTENT_DELETE}>
                <Button type="button" variant="outline" onClick={() => onDelete(entry)}>
                  {tCommon('action.delete')}
                </Button>
              </PermissionGate>
              {entry.status === 'draft' ? (
                <PermissionGate permission={PERMISSIONS.CONTENT_PUBLISH}>
                  <Button type="button" variant="secondary" loading={publishing} onClick={() => onPublish(entry.id)}>
                    {t('chronicle.publish')}
                  </Button>
                </PermissionGate>
              ) : (
                <PermissionGate permission={PERMISSIONS.CONTENT_PUBLISH}>
                  <Button type="button" variant="secondary" onClick={() => onUnpublish(entry)}>
                    {t('chronicle.unpublish')}
                  </Button>
                </PermissionGate>
              )}
              <PermissionGate permission={PERMISSIONS.CONTENT_EDIT}>
                <Button type="button" onClick={() => onEdit(entry)}>
                  {tCommon('action.edit')}
                </Button>
              </PermissionGate>
            </DialogFooter>
          </>
        ) : detail.isError ? (
          <DialogBody>
            <p className="text-body-sm text-danger-fg">
              {detail.error instanceof ApiError
                ? tErrors(detail.error.code, detail.error.params)
                : tErrors('UNEXPECTED')}
            </p>
          </DialogBody>
        ) : (
          <DialogBody>
            <div role="status" className="flex flex-col gap-4">
              <span className="sr-only">{tCommon('state.loading')}</span>
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-24 w-full" />
              <div className="grid grid-cols-4 gap-2">
                <Skeleton className="aspect-square" />
                <Skeleton className="aspect-square" />
                <Skeleton className="aspect-square" />
              </div>
            </div>
          </DialogBody>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ChronicleTimeline = ({
  rows,
  onOpen,
}: {
  rows: ChronicleEntry[];
  onOpen: (row: ChronicleEntry) => void;
}) => {
  const { t } = useTranslation('content');
  const years: { year: string; rows: ChronicleEntry[] }[] = [];
  for (const row of rows) {
    const year = row.occurredOn.slice(0, 4);
    const group = years.find((item) => item.year === year);
    if (group) group.rows.push(row);
    else years.push({ year, rows: [row] });
  }

  return (
    <div className="flex flex-col gap-8">
      {years.map((group) => (
        <section key={group.year}>
          <h2 className="mb-4 text-card-title font-semibold text-fg">{group.year}</h2>
          <ol className="relative ms-2 border-s border-border">
            {group.rows.map((row) => (
              <li key={row.id} className="relative pb-6 ps-6 last:pb-0">
                <span
                  className="absolute -start-1.5 top-1.5 size-3 rounded-full border-2 border-brand bg-background"
                  aria-hidden
                />
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-start gap-3 rounded-lg border border-border bg-surface p-4 text-start transition-colors hover:border-brand/40 hover:bg-surface-sunken/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  onClick={() => onOpen(row)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-caption text-fg-secondary">
                      {formatChronicleDate(row.occurredOn, row.datePrecision)}
                      {' · '}
                      {row.categoryName}
                    </p>
                    <p className="mt-1 text-body font-medium text-fg">{row.title}</p>
                    {row.body ? (
                      <p className="mt-2 line-clamp-3 text-body-sm text-fg-secondary">{row.body}</p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-wrap justify-end gap-2">
                    <Badge variant={row.status === 'published' ? 'success' : 'neutral'}>
                      {t(`chronicle.status.${row.status}`)}
                    </Badge>
                    <Badge variant="neutral">
                      {t(row.isPublic ? 'chronicle.visibility.public' : 'chronicle.visibility.private')}
                    </Badge>
                  </div>
                </button>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
};

interface MomentDialogProps {
  open: boolean;
  entryId: string | null;
  categories: ChronicleCategory[];
  canPublish: boolean;
  filePreviewOpen: boolean;
  onFilePreviewChange: (open: boolean) => void;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

const MomentDialog = ({
  open,
  entryId,
  categories,
  canPublish,
  filePreviewOpen,
  onFilePreviewChange,
  onOpenChange,
  onSaved,
}: MomentDialogProps) => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canCreateCategory = hasPermission(membership, PERMISSIONS.CONTENT_CREATE);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [precision, setPrecision] = useState<Precision>('day');
  const [day, setDay] = useState<string | null>(null);
  const [month, setMonth] = useState<string | null>(null);
  const [year, setYear] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [status, setStatus] = useState<StoryStatus>('draft');
  const [attachments, setAttachments] = useState<ChronicleAttachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<{ id: string; file: File; url: string }[]>([]);
  const [fileError, setFileError] = useState<string | undefined>();
  const [activeId, setActiveId] = useState<string | null>(null);
  const hydratedId = useRef<string | null>(null);
  const pendingFilesRef = useRef(pendingFiles);
  useEffect(() => {
    pendingFilesRef.current = pendingFiles;
  });
  useEffect(
    () => () => {
      for (const item of pendingFilesRef.current) URL.revokeObjectURL(item.url);
    },
    [],
  );

  const detail = useQuery({
    queryKey: ['content', 'chronicle', 'entry', entryId],
    enabled: open && entryId !== null,
    queryFn: () => api.get<ChronicleEntry>(`/content/chronicle/${entryId}`),
  });

  useEffect(() => {
    if (!open) {
      hydratedId.current = null;
      return;
    }
    if (!detail.data || detail.data.id !== entryId || hydratedId.current === detail.data.id) return;
    const entry = detail.data;
    hydratedId.current = entry.id;
    setActiveId(entry.id);
    setTitle(entry.title);
    setBody(entry.body ?? '');
    setCategoryId(entry.categoryId);
    setPrecision(entry.datePrecision);
    setIsPublic(entry.isPublic);
    setStatus(entry.status);
    setAttachments(entry.attachments);
    setPendingFiles((current) => {
      for (const item of current) URL.revokeObjectURL(item.url);
      return [];
    });
    setFileError(undefined);
    setYear(entry.occurredOn.slice(0, 4));
    setMonth(entry.occurredOn.slice(5, 7));
    setDay(entry.datePrecision === 'day' ? entry.occurredOn : null);
  }, [open, entryId, detail.data]);

  const resetCreate = () => {
    setTitle('');
    setBody('');
    setCategoryId(null);
    setPrecision('day');
    setDay(null);
    setMonth(null);
    setYear('');
    setIsPublic(false);
    setStatus('draft');
    setAttachments([]);
    setPendingFiles((current) => {
      for (const item of current) URL.revokeObjectURL(item.url);
      return [];
    });
    setFileError(undefined);
    setActiveId(null);
  };

  const occurredOn = (): string | null => {
    if (precision === 'year') return /^\d{4}$/.test(year.trim()) ? year.trim() : null;
    if (precision === 'month') {
      return /^\d{4}$/.test(year.trim()) && month ? `${year.trim()}-${month}` : null;
    }
    return day;
  };

  const fileCount = attachments.length + pendingFiles.length;
  const hydrating = open && entryId !== null && detail.isPending && !detail.data;
  const previewFiles: ChroniclePreviewFile[] = [
    ...attachments.map((file) => ({
      id: file.id,
      fileName: file.fileName,
      kind: file.kind,
      url: file.downloadUrl,
      contentType: file.contentType,
    })),
    ...pendingFiles.map((item) => ({
      id: item.id,
      fileName: item.file.name,
      kind: pendingFileKind(item.file),
      url: item.url,
      contentType: item.file.type,
    })),
  ];
  const dateValue = occurredOn();
  const canSave = title.trim().length > 0 && categoryId !== null && dateValue !== null;

  const reportError = (err: unknown) => {
    toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
  };

  const save = useMutation({
    mutationFn: async (publishAfter: boolean) => {
      if (!categoryId || dateValue === null) {
        throw new Error('incomplete');
      }
      const payload = {
        title: title.trim(),
        body: body.trim() ? body.trim() : null,
        categoryId,
        datePrecision: precision,
        occurredOn: dateValue,
        isPublic,
      };
      const saved = activeId
        ? await api.patch<ChronicleEntry>(`/content/chronicle/${activeId}`, payload)
        : await api.post<ChronicleEntry>('/content/chronicle', payload);
      setActiveId(saved.id);
      setStatus(saved.status);

      for (const item of pendingFiles) {
        const uploaded = await uploadBlob({
          purpose: 'chronicle_file',
          blob: item.file,
          fileName: item.file.name,
        });
        await api.post(`/content/chronicle/${saved.id}/attachments`, {
          fileKey: uploaded.key,
          fileName: item.file.name,
          contentType: uploaded.contentType,
        });
        setPendingFiles((current) => {
          for (const entry of current) {
            if (entry.id === item.id) URL.revokeObjectURL(entry.url);
          }
          return current.filter((entry) => entry.id !== item.id);
        });
      }

      if (publishAfter && saved.status === 'draft') {
        await api.post(`/content/chronicle/${saved.id}/publish`);
      }
    },
    onSuccess: (_result, publishAfter) => {
      toast.success(publishAfter ? t('chronicle.published') : t('chronicle.saved'));
      onSaved();
      onOpenChange(false);
      resetCreate();
    },
    onError: reportError,
  });

  const removeFile = useMutation({
    mutationFn: (attachmentId: string) => {
      if (!activeId) throw new Error('missing entry');
      return api.delete(`/content/chronicle/${activeId}/attachments/${attachmentId}`);
    },
    onSuccess: (_result, attachmentId) => {
      setAttachments((current) => current.filter((file) => file.id !== attachmentId));
      onSaved();
    },
    onError: reportError,
  });

  const createCategory = useMutation({
    mutationFn: () =>
      api.post<ChronicleCategory>('/content/chronicle/categories', {
        name: newCategoryName.trim(),
      }),
    onSuccess: (created) => {
      setCategoryId(created.id);
      setCreateCategoryOpen(false);
      setNewCategoryName('');
      toast.success(t('chronicle.categories.created'));
      void queryClient.invalidateQueries({ queryKey: ['content', 'chronicle', 'categories'] });
    },
    onError: reportError,
  });

  const categoryOptions = [
    ...categories.map((item) => ({ value: item.id, label: item.name })),
    ...(canCreateCategory
      ? [
          {
            value: CREATE_CATEGORY,
            label: t('chronicle.categories.add'),
            icon: <Plus className="size-4" aria-hidden />,
          },
        ]
      : []),
  ];

  const months = Array.from({ length: 12 }, (_, index) => {
    const value = monthValue(index + 1);
    return { value, label: chronicleMonthLabel(index + 1) };
  });

  return (
    <>
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) resetCreate();
      }}
    >
      <DialogContent
        size="lg"
        closeLabel={tCommon('action.close')}
        className="overflow-visible"
        {...previewDismissLock(filePreviewOpen)}
      >
        <DialogHeader>
          <DialogTitle>
            {entryId ? t('chronicle.dialog.editTitle') : t('chronicle.dialog.createTitle')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-6">
          {hydrating ? (
            <div role="status" className="flex flex-col gap-4">
              <span className="sr-only">{tCommon('state.loading')}</span>
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-28 w-full" />
            </div>
          ) : null}
          {hydrating ? null : (
          <>
          <FormField label={t('chronicle.fields.title')} required>
            {(field) => (
              <Input
                id={field.id}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                aria-describedby={field['aria-describedby']}
              />
            )}
          </FormField>
          <div className="flex flex-col gap-4">
            <FormField label={t('chronicle.fields.category')} required>
              {(field) => (
                <Listbox
                  id={field.id}
                  value={categoryId}
                  onChange={(value) => {
                    if (value === CREATE_CATEGORY) {
                      setNewCategoryName('');
                      setCreateCategoryOpen(true);
                      return;
                    }
                    setCategoryId(value);
                  }}
                  options={categoryOptions}
                  placeholder={t('chronicle.fields.category')}
                  emptyMessage={t('chronicle.categories.empty')}
                  popoverPortal="dialog"
                  aria-describedby={field['aria-describedby']}
                />
              )}
            </FormField>
            <FormField label={t('chronicle.fields.precision')} required>
              {(field) => (
                <Listbox
                  id={field.id}
                  value={precision}
                  onChange={(value) => {
                    if (value) setPrecision(value);
                  }}
                  options={PRECISIONS.map((item) => ({
                    value: item,
                    label: t(`chronicle.precision.${item}`),
                  }))}
                  popoverPortal="dialog"
                  aria-describedby={field['aria-describedby']}
                />
              )}
            </FormField>
            {precision === 'day' ? (
              <FormField label={t('chronicle.fields.date')} required>
                {(field) => (
                  <DatePicker
                    id={field.id}
                    value={day}
                    onChange={setDay}
                    popoverPortal="dialog"
                    aria-describedby={field['aria-describedby']}
                  />
                )}
              </FormField>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {precision === 'month' ? (
                  <FormField label={t('chronicle.fields.month')} required>
                    {(field) => (
                      <Listbox
                        id={field.id}
                        value={month}
                        onChange={setMonth}
                        options={months}
                        placeholder={t('chronicle.fields.month')}
                        popoverPortal="dialog"
                        aria-describedby={field['aria-describedby']}
                      />
                    )}
                  </FormField>
                ) : null}
                <FormField label={t('chronicle.fields.year')} required>
                  {(field) => (
                    <Input
                      id={field.id}
                      inputMode="numeric"
                      value={year}
                      onChange={(event) => setYear(event.target.value.replace(/\D/g, '').slice(0, 4))}
                      aria-describedby={field['aria-describedby']}
                    />
                  )}
                </FormField>
              </div>
            )}
          </div>
          <FormField label={t('chronicle.fields.body')}>
            {(field) => (
              <Textarea
                id={field.id}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                rows={5}
                aria-describedby={field['aria-describedby']}
              />
            )}
          </FormField>
          <SwitchField
            id="chronicle-moment-public"
            label={t('chronicle.fields.publicLabel')}
            description={t('chronicle.fields.publicDescription')}
            checked={isPublic}
            onCheckedChange={setIsPublic}
          />
          <FormField label={t('chronicle.fields.files')} error={fileError}>
            {() => (
              <div className="flex flex-col gap-3">
                <FileDropzone
                  multiple
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.pdf,.doc,.docx"
                  acceptLabel={t('chronicle.file.accept')}
                  maxBytes={MAX_FILE_BYTES}
                  disabled={fileCount >= MAX_FILES || save.isPending}
                  error={fileError}
                  labels={{ prompt: t('chronicle.file.prompt'), remove: t('chronicle.file.remove') }}
                  onFiles={(files) => {
                    const room = MAX_FILES - attachments.length - pendingFiles.length;
                    if (room <= 0) {
                      setFileError(t('chronicle.file.tooMany'));
                      return;
                    }
                    const next = files.slice(0, room);
                    if (next.length < files.length) setFileError(t('chronicle.file.tooMany'));
                    else setFileError(undefined);
                    setPendingFiles((current) => [
                      ...current,
                      ...next.map((file) => ({
                        id: crypto.randomUUID(),
                        file,
                        url: URL.createObjectURL(file),
                      })),
                    ]);
                  }}
                />
                <ChronicleFileTiles
                  files={previewFiles}
                  removingId={removeFile.isPending ? removeFile.variables : null}
                  onPreviewChange={onFilePreviewChange}
                  onRemove={(id) => {
                    if (pendingFiles.some((item) => item.id === id)) {
                      setPendingFiles((current) => {
                        for (const item of current) {
                          if (item.id === id) URL.revokeObjectURL(item.url);
                        }
                        return current.filter((item) => item.id !== id);
                      });
                      return;
                    }
                    removeFile.mutate(id);
                  }}
                />
              </div>
            )}
          </FormField>
          </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={save.isPending}
          >
            {tCommon('action.cancel')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!canSave || save.isPending || hydrating}
            loading={save.isPending && save.variables === false}
            onClick={() => save.mutate(false)}
          >
            {status === 'published' ? t('chronicle.save') : t('chronicle.saveDraft')}
          </Button>
          {canPublish && status !== 'published' ? (
            <Button
              type="button"
              disabled={!canSave || save.isPending || hydrating}
              loading={save.isPending && save.variables === true}
              onClick={() => save.mutate(true)}
            >
              {t('chronicle.publish')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <FormDialog
      open={createCategoryOpen}
      onOpenChange={setCreateCategoryOpen}
      title={t('chronicle.categories.addTitle')}
      size="sm"
      submitLabel={t('chronicle.categories.add')}
      cancelLabel={tCommon('action.cancel')}
      submitting={createCategory.isPending}
      submitDisabled={newCategoryName.trim().length === 0}
      onSubmit={() => createCategory.mutate()}
    >
      <FormField label={t('chronicle.categories.name')} required>
        {(field) => (
          <Input
            id={field.id}
            value={newCategoryName}
            onChange={(event) => setNewCategoryName(event.target.value)}
            aria-describedby={field['aria-describedby']}
          />
        )}
      </FormField>
    </FormDialog>
    </>
  );
};
